import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore, authSelectors } from '@/lib/stores/authStore';
import { Spinner } from '@/components/base/Spinner';
import { Body } from '@/components/base/Typography';
import type { Capability } from '@/lib/types/authorization';
import { useCan } from '@/lib/hooks/useCan';

/**
 * ProtectedRoute Props
 */
interface ProtectedRouteProps {
  /**
   * Child components to render if authenticated
   */
  children: React.ReactNode;

  /**
   * Required role to access this route (optional)
   */
  requiredRole?: string;

  /**
   * Capability the user's role must have to open this route (optional), e.g.
   * CAPABILITIES.CONSULTATION_START
   */
  requiredCapability?: Capability;

  /**
   * Custom redirect path (default: /login)
   */
  redirectTo?: string;

  /**
   * Custom unauthorized message
   */
  unauthorizedMessage?: string;
}

/**
 * ProtectedRoute Component
 *
 * Guards routes that require authentication. Features:
 * - Redirects to login page if not authenticated
 * - Preserves intended destination for post-login redirect
 * - Optional role checking
 * - Loading state during auth initialization
 * - Accessible error messages
 *
 * @example
 * ```tsx
 * // Basic usage - require authentication
 * <Route
 *   path="/records"
 *   element={
 *     <ProtectedRoute>
 *       <RecordsPage />
 *     </ProtectedRoute>
 *   }
 * />
 *
 * // Require specific role
 * <Route
 *   path="/settings"
 *   element={
 *     <ProtectedRoute requiredRole="admin">
 *       <SettingsPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */
export function ProtectedRoute({
  children,
  requiredRole,
  requiredCapability,
  redirectTo = '/login',
  unauthorizedMessage = 'You do not have permission to access this page.',
}: ProtectedRouteProps) {
  const location = useLocation();
  const isAuthenticated = useAuthStore(authSelectors.isAuthenticated);
  const user = useAuthStore(authSelectors.user);
  const { hasRole, canAccessRoute } = useAuthStore();
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const hasRequiredCapability = useCan(requiredCapability);

  // Show loading state while auth is being initialized from localStorage
  if (!isInitialized) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        role="status"
        aria-live="polite"
        aria-label="Loading authentication state"
      >
        <div className="text-center">
          <Spinner size="lg" />
          <Body className="mt-4 text-muted-foreground">Loading...</Body>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Preserve the intended destination for post-login redirect
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check route-based access from the configured menu.
  if (!canAccessRoute(location.pathname)) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="mb-4 text-6xl">🔒</div>
          <h1 className="mb-2 font-display text-2xl font-bold text-foreground">Access Denied</h1>
          <Body className="mb-6 text-muted-foreground">You don't have access to this page.</Body>
          <Body className="text-sm text-muted-foreground">
            Current role:{' '}
            <code className="rounded bg-muted px-1.5 py-0.5">{user?.role || 'none'}</code>
          </Body>
          <Body className="mt-4">
            <a href="/dashboard" className="text-pranik-brand hover:underline">
              ← Return to Dashboard
            </a>
          </Body>
        </div>
      </div>
    );
  }

  // Check required role
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="mb-4 text-6xl">🔒</div>
          <h1 className="mb-2 font-display text-2xl font-bold text-foreground">Access Denied</h1>
          <Body className="mb-6 text-muted-foreground">{unauthorizedMessage}</Body>
          <Body className="text-sm text-muted-foreground">
            Required role: <code className="rounded bg-muted px-1.5 py-0.5">{requiredRole}</code>
          </Body>
          <Body className="mt-2 text-sm text-muted-foreground">
            Current user: <strong>{user?.username}</strong> ({user?.role || 'no role'})
          </Body>
        </div>
      </div>
    );
  }

  // Check required capability (what the role may do, from its AccessPolicy)
  if (!hasRequiredCapability) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="mb-4 text-6xl">🔒</div>
          <h1 className="mb-2 font-display text-2xl font-bold text-foreground">Access Denied</h1>
          <Body className="mb-6 text-muted-foreground">Your role is not allowed to do this.</Body>
          <Body className="mt-4">
            <a href="/dashboard" className="text-pranik-brand hover:underline">
              ← Return to Dashboard
            </a>
          </Body>
        </div>
      </div>
    );
  }

  // Render children if all checks pass
  return <>{children}</>;
}

/**
 * Hook to access protected route utilities
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { canAccess, canAccessRoute } = useProtectedRoute();
 *
 *   if (!canAccess('patients:write')) {
 *     return <div>You cannot edit patients</div>;
 *   }
 *
 *   return <PatientForm />;
 * }
 * ```
 */
export function useProtectedRoute() {
  const { hasRole, isAuthenticated, canAccessRoute } = useAuthStore();

  return {
    /**
     * Check if user has role
     */
    hasRole,

    /**
     * Check if user is authenticated
     */
    isAuthenticated: isAuthenticated(),

    /**
     * Check if user can access a specific route
     */
    canAccessRoute,
  };
}
